/**
 * 編集者と共有する OneDrive（Issue #943）。ダッシュボードに置く。
 * つなぐ（admin。Microsoft のページでコードを入れる → PollOneDriveLink が受け取るまで待つ）・置き場所・カード画像の同期（1 日 1 回は本番が自分で）。
 * 書き出した動画は書き出しが終わると本番が自分で上げる。手で上げるのは OneDriveUploadButton（かき出しの一覧・手札の書き出し）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/api';
import { formatServerTime } from '../utils/serverTime';

export const POLL_MS = 15000;

const SYNC_TEXT = {
  processing: '同期中',
  completed:  '同期済み',
  failed:     '失敗',
  cancelled:  'やめた',
};

export function syncText(sync) {
  if (!sync) return 'まだ同期していません';
  const when = sync.completed_at || sync.created_at;
  const at = formatServerTime(when);
  const label = SYNC_TEXT[sync.status] ?? sync.status;
  const detail = sync.status === 'failed' ? sync.error_message : sync.progress;
  return `${label}${at ? `（${at}）` : ''}${detail ? `: ${detail}` : ''}`;
}

export default function OneDrivePanel() {
  const [st, setSt] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);       // つなぎかけ { user_code, verification_uri, interval }
  const [dirs, setDirs] = useState(null);       // 置き場所の編集中の値
  const pollTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const s = await api.project('GetOneDriveStatus', {});
      setSt(s);
      setError(null);
      if (s.pending) setCode((c) => c ?? { ...s.pending, interval: 5 });
    } catch (e) {
      setError(String(e?.message ?? e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => { clearInterval(t); clearTimeout(pollTimer.current); };
  }, [load]);

  // コードを出しているあいだは、許可されたかを本番に聞き続ける
  useEffect(() => {
    if (!code) return undefined;
    const tick = async () => {
      try {
        const res = await api.project('PollOneDriveLink', {});
        if (res.state === 'pending') {
          pollTimer.current = setTimeout(tick, (code.interval || 5) * 1000);
          return;
        }
        setCode(null);
        load();
      } catch (e) {
        setCode(null);
        setError(`つなげませんでした（${e?.message ?? e}）。もう一度「つなぐ」から`);
      }
    };
    pollTimer.current = setTimeout(tick, (code.interval || 5) * 1000);
    return () => clearTimeout(pollTimer.current);
  }, [code, load]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const startLink = () => run(async () => setCode(await api.project('StartOneDriveLink', {})));
  const unlink = () => {
    if (!window.confirm('OneDrive とのつなぎを切りますか？（自動の同期とアップロードが止まります）')) return;
    run(() => api.project('UnlinkOneDrive', {}));
  };
  const syncCards = () => run(() => api.project('CreateOneDriveCardSyncRequest', {}));
  const saveDirs = () => run(async () => {
    await api.project('SetOneDriveFolders', dirs);
    setDirs(null);
  });

  const syncing = st?.card_sync?.status === 'processing';

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="onedrive-panel">
      <div className="card-title" style={{ marginBottom: 8 }}>OneDrive（編集者と共有）</div>
      {st === null ? <div className="card-meta">{error ? `読めません（${error}）` : '読んでいます…'}</div> : (
        <>
          {!st.configured && (
            <div className="card-meta" data-testid="onedrive-not-configured">
              本番に ONEDRIVE_CLIENT_ID がありません（Entra に登録したアプリのクライアント ID を server/.env へ）
            </div>
          )}
          {st.configured && !st.linked && !code && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="card-meta">つないでいません</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={startLink} disabled={busy}>つなぐ</button>
            </div>
          )}
          {code && (
            <div data-testid="onedrive-code">
              <div>
                <a href={code.verification_uri} target="_blank" rel="noreferrer">{code.verification_uri}</a> を開いて、
                次のコードを入れてください（共有しているフォルダーの持ち主の Microsoft アカウントで）
              </div>
              <code style={{ display: 'inline-block', fontSize: 22, letterSpacing: 2, margin: '6px 0', color: '#cfe2ff' }}>
                {code.user_code}
              </code>
              <div className="card-meta">許可されるのを待っています…</div>
            </div>
          )}
          {st.linked && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2ecc71' }} />
                <span>{st.account ?? '（名前なし）'} の OneDrive</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={unlink} disabled={busy}
                  style={{ marginLeft: 'auto' }}>切る</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span className="card-meta" style={{ flex: 1, minWidth: 0 }} data-testid="onedrive-card-sync">
                  カード画像: {syncText(st.card_sync)}
                </span>
                <button type="button" className="btn btn-primary btn-sm" onClick={syncCards} disabled={busy || syncing}>
                  {syncing ? '同期中…' : '今すぐ同期'}
                </button>
              </div>
            </>
          )}
          {st.configured && (
            dirs ? (
              <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                <label className="card-meta">カード画像の同期先
                  <input className="form-input" value={dirs.card_dir}
                    onChange={(e) => setDirs({ ...dirs, card_dir: e.target.value })} />
                </label>
                <label className="card-meta">書き出した動画の置き場所
                  <input className="form-input" value={dirs.video_dir}
                    onChange={(e) => setDirs({ ...dirs, video_dir: e.target.value })} />
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={saveDirs} disabled={busy}>保存</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDirs(null)}>やめる</button>
                </div>
              </div>
            ) : (
              <div className="card-meta" style={{ marginTop: 8 }}>
                カード: {st.card_dir} ／ 動画: {st.video_dir}/{'{デュエル名}_{id}/{試合名}_{id}/'}{' '}
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setDirs({ card_dir: st.card_dir, video_dir: st.video_dir })}>置き場所を変える</button>
              </div>
            )
          )}
        </>
      )}
      {error && st !== null ? <div role="status" style={{ color: '#f99', marginTop: 8 }}>{error}</div> : null}
    </div>
  );
}

/** 終わった書き出しの動画を OneDrive へ（本番は書き出しが終わると自分で上げる。これは上げ直し・つなぐ前の分） */
export function OneDriveUploadButton({ requestId }) {
  const [state, setState] = useState(null);   // null / 'busy' / 'queued' / 'error'
  const upload = async () => {
    setState('busy');
    try {
      await api.project('CreateOneDriveVideoUploadRequest', { request_id: requestId });
      setState('queued');
    } catch {
      setState('error');
      alert('OneDrive へ上げられませんでした（ダッシュボードで OneDrive につないでいるか確かめてください）');
    }
  };
  return (
    <button type="button" className="btn btn-sm btn-secondary" onClick={upload}
      disabled={state === 'busy' || state === 'queued'} style={{ marginLeft: 4 }}>
      {state === 'queued' ? 'OneDrive へ上げています' : state === 'busy' ? '...' : 'OneDrive へ'}
    </button>
  );
}
